import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2';
import React, { useMemo } from 'react';
import {
  CREATE_CONTACT_ADD_COMPANY_LOGO,
  CREATE_CONTACT_ADD_PROFILE_PHOTO,
} from '~/generated/AnalyticsTypes';
import useAnalytics from '~/react-hooks/use-analytics';
import { Contact, SocialLinks } from '~/types/models';
import { ImageType } from './Image';
import ImageUpload from './ImageUpload';
import ProfileForm, { ProfileFormProps } from './ProfileForm';
import ProfileLinks from './ProfileLinks';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import cx from 'classnames';

export interface ContactModalProps extends Pick<ProfileFormProps, 'errors'> {
  contact: Contact | null;
  open: boolean;
  confirmButtonText: string;
  heading: string;
  loading?: boolean;
  disableSaveButton?: boolean;
  onCloseClick: () => void;
  onProfileChange: (contact: Contact) => void;
  onConfirmClick: () => void;
  className?: string;
}

export default function ContactModal({
  contact,
  open,
  heading,
  loading = false,
  disableSaveButton = false,
  onCloseClick,
  onConfirmClick,
  onProfileChange,
  confirmButtonText,
  errors,
  className,
}: ContactModalProps) {
  const analytics = useAnalytics();
  const handleProfileOnChange = (
    profileKey: string,
    value: string | SocialLinks
  ) => {
    const updatedContact = { ...contact } as Contact;

    const propertyValue =
      typeof value === 'object'
        ? ({
            ...updatedContact[profileKey],
            ...value,
          } as Contact)
        : value;

    updatedContact[profileKey] = propertyValue;

    switch (profileKey as keyof Contact) {
      case 'profile_photo':
        analytics.track(CREATE_CONTACT_ADD_PROFILE_PHOTO);
        break;
      case 'company_logo':
        analytics.track(CREATE_CONTACT_ADD_COMPANY_LOGO);
        break;
    }

    onProfileChange(updatedContact);
  };

  const modalProps = useMemo<ModalProps>(() => {
    return {
      open,
      onModalClose: onCloseClick,
      className: cx(className, 'max-w-[733px] !p-[30px]'),
      overlayClassName: '!z-[9950]',
      closeButtonClassName: '!w-5',
      hideActions: true,
      hideCloseButton: true,
    };
  }, [open]);

  return (
    <Modal {...modalProps}>
      {heading && (
        <h1 className='my-0 mx-auto text-xl font-bold v2-text-neutral-900 mb-5'>
          {heading}
        </h1>
      )}
      <div className='flex'>
        <ImageUpload
          imgUrl={contact?.image}
          className='mr-7'
          imageType={ImageType.Circle}
          label='Profile Image'
          onImageChange={handleProfileOnChange}
          profileKey='profile_photo'
        />
        <ImageUpload
          imageType={ImageType.Square}
          label='Company Logo'
          imgUrl={contact?.company_logo ?? undefined}
          onImageChange={handleProfileOnChange}
          profileKey='company_logo'
        />
      </div>
      <div className='flex mt-5'>
        <ProfileForm
          onProfileChange={handleProfileOnChange}
          profile={contact}
          errors={errors}
        />
        <ProfileLinks
          onChange={handleProfileOnChange}
          className='ml-7 w-80'
          links={contact?.social_links}
        />
      </div>

      <div className='flex flex gap-x-5 justify-end'>
        <Button
          className='py-1.5'
          variant={ButtonVariant.Secondary}
          onClick={onCloseClick}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className='py-1.5'
          variant={ButtonVariant.Primary}
          onClick={onConfirmClick}
          disabled={disableSaveButton || loading}
        >
          {confirmButtonText}
        </Button>
      </div>
    </Modal>
  );
}
